import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Printer,
  ReceiptIndianRupee,
  Wallet,
  X,
  ArrowLeftRight,
  CircleDollarSign,
} from "lucide-react";
import { getPosCloseReport } from "../../../services/pos";

const money = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const labelForMethod = (method = "") =>
  method
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const dateTime = (value) =>
  value ? new Date(value).toLocaleString("en-IN") : "—";

function printCloseReport(report, actualCash) {
  const expectedCash = Number(report?.session?.expectedCash || 0);
  const actual = Number(actualCash || 0);
  const variance = actual - expectedCash;

  const paymentRows = Object.entries(report?.payments?.netByMethod || {})
    .map(
      ([method, amount]) => `
        <tr>
          <td>${labelForMethod(method)}</td>
          <td style="text-align:right">${money(
            report?.payments?.salesByMethod?.[method] || 0,
          )}</td>
          <td style="text-align:right">${money(
            report?.payments?.refundsByMethod?.[method] || 0,
          )}</td>
          <td style="text-align:right"><strong>${money(amount)}</strong></td>
        </tr>
      `,
    )
    .join("");

  const printWindow = window.open("", "_blank", "width=900,height=1000");

  if (!printWindow) return;

  printWindow.document.write(`
    <!doctype html>
    <html>
      <head>
        <title>Shift Close Report ${report.session.sessionNumber}</title>
        <style>
          * { box-sizing: border-box; }

          body {
            margin: 0;
            padding: 30px;
            font-family: Arial, sans-serif;
            color: #172033;
            font-size: 13px;
          }

          .report {
            max-width: 800px;
            margin: auto;
          }

          .header {
            text-align: center;
            border-bottom: 2px solid #7a2238;
            padding-bottom: 18px;
          }

          h1 {
            margin: 0;
            font-size: 25px;
            color: #7a2238;
          }

          h2 {
            margin: 8px 0 0;
            font-size: 16px;
          }

          .muted {
            color: #667085;
          }

          .grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
            margin: 20px 0;
          }

          .box {
            background: #f8f6f5;
            padding: 12px;
            border-radius: 8px;
          }

          .label {
            font-size: 11px;
            color: #667085;
            text-transform: uppercase;
          }

          .value {
            margin-top: 5px;
            font-size: 16px;
            font-weight: bold;
          }

          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 18px;
          }

          th {
            background: #f2f4f7;
            text-align: left;
            padding: 10px;
          }

          td {
            padding: 10px;
            border-bottom: 1px solid #e4e7ec;
          }

          .footer {
            margin-top: 28px;
            border-top: 1px dashed #98a2b3;
            padding-top: 15px;
            text-align: center;
            color: #667085;
          }
        </style>
      </head>

      <body>
        <div class="report">
          <div class="header">
            <h1>CHARMS</h1>
            <h2>POS Shift Close Report</h2>

            <p class="muted">
              Session #${report.session.sessionNumber} ·
              ${report.session.counter.store.name} ·
              ${report.session.counter.name}
            </p>
          </div>

          <div class="grid">
            <div class="box">
              <div class="label">Cashier</div>
              <div class="value">
                ${
                  report.session.openedBy.employee?.fullName ||
                  report.session.openedBy.email ||
                  "—"
                }
              </div>
            </div>

            <div class="box">
              <div class="label">Opened At</div>
              <div class="value">${dateTime(report.session.openedAt)}</div>
            </div>

            <div class="box">
              <div class="label">Gross Sales</div>
              <div class="value">${money(report.summary.grossSalesAmount)}</div>
            </div>

            <div class="box">
              <div class="label">Exchange Collection</div>
              <div class="value">${money(
                report.summary.exchangeCollectedAmount,
              )}</div>
            </div>

            <div class="box">
              <div class="label">Refunds</div>
              <div class="value">${money(
                report.summary.totalRefundAmount,
              )}</div>
            </div>

            <div class="box">
              <div class="label">Net Sales</div>
              <div class="value">${money(report.summary.netSalesAmount)}</div>
            </div>

            <div class="box">
              <div class="label">Expected Cash</div>
              <div class="value">${money(expectedCash)}</div>
            </div>

            <div class="box">
              <div class="label">Actual Cash</div>
              <div class="value">${money(actual)}</div>
            </div>

            <div class="box">
              <div class="label">Cash Variance</div>
              <div class="value">${money(variance)}</div>
            </div>

            <div class="box">
              <div class="label">Transactions</div>
              <div class="value">
                ${report.summary.saleCount} sales ·
                ${report.summary.returnCount} returns ·
                ${report.summary.exchangeCount || 0} exchanges
              </div>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>Payment Method</th>
                <th style="text-align:right">Collected</th>
                <th style="text-align:right">Refunded</th>
                <th style="text-align:right">Net</th>
              </tr>
            </thead>

            <tbody>${paymentRows}</tbody>
          </table>

          <div class="footer">
            System-generated POS shift reconciliation report.
          </div>
        </div>

        <script>
          window.onload = () => {
            window.print();
            window.onafterprint = () => window.close();
          };
        </script>
      </body>
    </html>
  `);

  printWindow.document.close();
}

export default function PosCloseShiftModal({
  open,
  session,
  accessToken,
  actualCash,
  setActualCash,
  saving,
  onConfirm,
  onClose,
}) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !session?.id) return undefined;

    let active = true;

    async function loadReport() {
      setLoading(true);
      setError("");

      try {
        const data = await getPosCloseReport(session.id, accessToken);

        if (active) {
          setReport(data);
        }
      } catch (requestError) {
        if (active) {
          setError(
            requestError.message || "Unable to load shift reconciliation",
          );
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadReport();

    return () => {
      active = false;
    };
  }, [accessToken, open, session?.id]);

  const expectedCash = Number(
    report?.session?.expectedCash ?? session?.expectedCash ?? 0,
  );

  const enteredActualCash =
    actualCash === "" ? expectedCash : Number(actualCash || 0);

  const variance = useMemo(
    () => enteredActualCash - expectedCash,
    [enteredActualCash, expectedCash],
  );

  const cashReconciliationDifference = Number(
    report?.cashReconciliation?.difference ?? 0,
  );

  const paymentRows = Object.entries(report?.payments?.netByMethod || {});

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 p-4 backdrop-blur-sm">
      <div className="mx-auto my-6 w-full max-w-6xl rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-7 py-5">
          <div className="flex items-center gap-3">
            <div className="grid h-11 w-11 place-items-center rounded-2xl bg-primary-50 text-primary-800">
              <ReceiptIndianRupee size={22} />
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                Close Shift Reconciliation
              </h2>

              <p className="text-sm text-gray-500">
                Review sales, returns, exchanges, payment methods, and cash.
              </p>
            </div>
          </div>

          <button
            type="button"
            disabled={saving}
            onClick={onClose}
            className="grid h-10 w-10 place-items-center rounded-xl bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-7">
          {error && (
            <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {loading || !report ? (
            <div className="py-20 text-center text-gray-500">
              Loading shift reconciliation…
            </div>
          ) : (
            <>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
                <SummaryCard
                  label="Gross Sales"
                  value={money(report.summary.grossSalesAmount)}
                  description={`${report.summary.saleCount} completed sale${
                    report.summary.saleCount === 1 ? "" : "s"
                  }`}
                  className="bg-[#F8F6F5]"
                />

                <SummaryCard
                  label="Exchange Settlement"
                  value={money(
                    Number(report.summary.exchangeCollectedAmount || 0) -
                      Number(report.summary.exchangeRefundAmount || 0),
                  )}
                  description={`${report.summary.exchangeCount || 0} exchange${
                    report.summary.exchangeCount === 1 ? "" : "s"
                  }`}
                  className="bg-violet-50"
                />

                <SummaryCard
                  label="Refunds"
                  value={money(report.summary.totalRefundAmount)}
                  description={`${report.summary.returnCount} return${
                    report.summary.returnCount === 1 ? "" : "s"
                  }`}
                  className="bg-red-50"
                  valueClassName="text-red-700"
                />

                <SummaryCard
                  label="Net Sales"
                  value={money(report.summary.netSalesAmount)}
                  description="Sales after returns and exchanges"
                  className="bg-primary-50"
                  valueClassName="text-primary-800"
                />

                <SummaryCard
                  label="Expected Cash"
                  value={money(expectedCash)}
                  description="Opening cash + cash collected − cash refunded"
                  className="bg-emerald-50"
                  valueClassName="text-emerald-800"
                />
              </div>

              {Math.abs(cashReconciliationDifference) >= 0.01 && (
                <div className="mt-5 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-800">
                  <AlertTriangle className="mt-0.5 shrink-0" size={18} />

                  <div>
                    <p className="font-bold">
                      System cash reconciliation needs review
                    </p>

                    <p className="mt-1">
                      Transaction payments and recorded expected cash differ by{" "}
                      {money(cashReconciliationDifference)}. Do not close the
                      session until this is understood.
                    </p>
                  </div>
                </div>
              )}

              <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200">
                <div className="flex items-center gap-3 border-b border-slate-200 bg-slate-100 px-5 py-3">
                  <CircleDollarSign size={18} className="text-primary-800" />

                  <div>
                    <h3 className="font-bold text-slate-800">
                      Payment Reconciliation
                    </h3>

                    <p className="text-xs text-slate-500">
                      Positive values are collections. Negative values are
                      refunds.
                    </p>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full min-w-[650px] text-sm">
                    <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                      <tr>
                        <th className="px-5 py-3">Method</th>
                        <th className="px-5 py-3 text-right">Collected</th>
                        <th className="px-5 py-3 text-right">Refunded</th>
                        <th className="px-5 py-3 text-right">Net Collected</th>
                      </tr>
                    </thead>

                    <tbody>
                      {paymentRows.map(([method, netAmount]) => (
                        <tr key={method} className="border-t border-slate-100">
                          <td className="px-5 py-3 font-semibold text-gray-800">
                            {labelForMethod(method)}
                          </td>

                          <td className="px-5 py-3 text-right text-gray-700">
                            {money(
                              report.payments.salesByMethod?.[method] || 0,
                            )}
                          </td>

                          <td className="px-5 py-3 text-right text-red-600">
                            -
                            {money(
                              report.payments.refundsByMethod?.[method] || 0,
                            )}
                          </td>

                          <td
                            className={`px-5 py-3 text-right font-bold ${
                              Number(netAmount) < 0
                                ? "text-red-700"
                                : "text-primary-800"
                            }`}
                          >
                            {money(netAmount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mt-6 grid gap-5 lg:grid-cols-2">
                <div className="rounded-2xl border border-gray-200 p-5">
                  <div className="flex items-center gap-3">
                    <Wallet className="text-primary-800" />

                    <div>
                      <h3 className="font-bold text-gray-900">
                        Physical Cash Count
                      </h3>

                      <p className="text-sm text-gray-500">
                        Count all cash in the drawer before ending the shift.
                      </p>
                    </div>
                  </div>

                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={actualCash}
                    onChange={(event) => setActualCash(event.target.value)}
                    className="mt-5 h-14 w-full rounded-xl border border-gray-200 px-4 text-xl font-bold outline-none focus:border-primary-800"
                    placeholder="Enter actual cash"
                  />
                </div>

                <div
                  className={`rounded-2xl border p-5 ${
                    Math.abs(variance) < 0.01
                      ? "border-emerald-200 bg-emerald-50"
                      : "border-amber-200 bg-amber-50"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {Math.abs(variance) < 0.01 ? (
                      <CheckCircle2 className="text-emerald-700" />
                    ) : (
                      <AlertTriangle className="text-amber-700" />
                    )}

                    <div>
                      <p className="font-bold text-gray-900">
                        {Math.abs(variance) < 0.01
                          ? "Cash is balanced"
                          : variance > 0
                            ? "Cash excess"
                            : "Cash shortage"}
                      </p>

                      <p className="mt-1 text-sm text-gray-600">
                        Expected: {money(expectedCash)} · Actual:{" "}
                        {money(enteredActualCash)}
                      </p>
                    </div>
                  </div>

                  <p
                    className={`mt-5 text-3xl font-bold ${
                      Math.abs(variance) < 0.01
                        ? "text-emerald-700"
                        : "text-amber-700"
                    }`}
                  >
                    {variance >= 0 ? "+" : ""}
                    {money(variance)}
                  </p>
                </div>
              </div>

              <div className="mt-7 flex flex-col-reverse justify-end gap-3 border-t border-gray-100 pt-6 sm:flex-row">
                <button
                  type="button"
                  onClick={() => printCloseReport(report, enteredActualCash)}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-primary-200 px-5 py-3 font-semibold text-primary-800 hover:bg-primary-50"
                >
                  <Printer size={17} />
                  Print Report
                </button>

                <button
                  type="button"
                  disabled={saving || actualCash === ""}
                  onClick={onConfirm}
                  className="rounded-xl bg-primary-800 px-6 py-3 font-semibold text-white disabled:opacity-50"
                >
                  {saving ? "Closing Shift…" : "Confirm End Shift"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  description,
  className,
  valueClassName = "text-gray-900",
}) {
  return (
    <div className={`rounded-2xl p-5 ${className}`}>
      <p className="text-xs font-semibold uppercase text-gray-500">{label}</p>

      <p className={`mt-2 text-2xl font-bold ${valueClassName}`}>{value}</p>

      <p className="mt-1 text-xs text-gray-500">{description}</p>
    </div>
  );
}
